import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash, Lightning, Play, PencilSimple, Eye, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import AccountTabs from "@/components/AccountTabs";
import useAccountScope from "@/hooks/useAccountScope";

const CATEGORIES = [
  { value: "__any__", label: "N/A (any category)" },
  { value: "promotions", label: "Promotions" },
  { value: "newsletters", label: "Newsletters" },
  { value: "social", label: "Social" },
  { value: "updates", label: "Updates" },
  { value: "receipts", label: "Receipts" },
];

const EMPTY_FORM = {
  name: "",
  sender_contains: "__any__",
  customSender: false,
  older_than_days: 30,
  category: "__any__",
  active: true,
};

export default function Rules() {
  const { accounts, selectedAccountId, accountParam, selectedAccount, setSelectedAccountId } = useAccountScope();
  const [rules, setRules] = useState([]);
  const [topSenders, setTopSenders] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null); // { rule_name, match_count, samples, ... }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const load = async () => {
    try {
      const params = accountParam ? { account_id: accountParam } : {};
      const { data } = await api.get("/rules", { params });
      setRules(data);

      // Also fetch top senders for the dropdown
      const { data: senders } = await api.get("/analytics/top-senders", { params });
      setTopSenders(senders);
    } catch (err) {
      console.error("Failed to load rules:", err);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await load();
      } finally {
        setIsLoading(false);
      }
    })();
  }, [accountParam]);

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">Loading…</div>;
  }

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    const days = parseInt(form.older_than_days, 10);
    if (!Number.isFinite(days) || days < 1) {
      return toast.error("Older-than-days must be at least 1");
    }
    // Serialize sentinel "__any__" to empty string for backend.
    const payload = {
      name: form.name.trim(),
      account_id: accountParam,
      sender_contains: form.sender_contains === "__any__" ? "" : form.sender_contains,
      older_than_days: days,
      category: form.category === "__any__" ? "" : form.category,
      active: form.active,
    };
    try {
      if (editingId) {
        await api.patch(`/rules/${editingId}`, payload);
        toast.success("Rule updated");
      } else {
        await api.post("/rules", payload);
        toast.success("Rule created");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const beginEdit = (r) => {
    setEditingId(r.rule_id);
    setForm({
      name: r.name || "",
      sender_contains: r.sender_contains && r.sender_contains !== "" ? r.sender_contains : "__any__",
      customSender: r.sender_contains && !topSenders.some(s => (s.sender_email || s.sender_name) === r.sender_contains),
      older_than_days: r.older_than_days || 30,
      category: r.category && r.category !== "" ? r.category : "__any__",
      active: r.active !== false,
    });
    document.querySelector('[data-testid="rule-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const remove = async (id) => {
    await api.delete(`/rules/${id}`);
    toast.success("Deleted");
    if (editingId === id) resetForm();
    load();
  };

  const toggle = async (id) => {
    await api.post(`/rules/${id}/toggle`);
    load();
  };

  const runNow = async () => {
    try {
      const params = accountParam ? { account_id: accountParam } : {};
      const { data } = await api.post("/rules/run-now", null, { params });
      toast.success(`Scheduler executed — ${data.deleted} emails deleted`);
    } catch (e) {
      toast.error("Failed to run rules");
    }
  };

  const dryRun = async (rule) => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post(`/rules/${rule.rule_id}/dry-run`);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to preview rule");
    } finally {
      setPreviewLoading(false);
    }
  };

  const senderDisplay = (r) => {
    if (!r.sender_contains) return "any sender";
    return `sender~"${r.sender_contains}"`;
  };
  const categoryDisplay = (r) => (r.category ? r.category : "any category");

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="section-tag mb-2">/ Auto-rules</div>
          <h1 className="font-display text-3xl sm:text-4xl">Set it and forget it</h1>
          <AccountTabs
            accounts={accounts}
            selectedAccountId={selectedAccountId}
            onSelect={setSelectedAccountId}
            className="mt-4"
          />
          <p className="text-xs uppercase tracking-widest font-mono text-muted-foreground mt-2">
            Viewing: {selectedAccount ? (selectedAccount.email || selectedAccount.account_id) : "All Accounts"}
          </p>
          <p className="text-muted-foreground mt-1">
            Background scheduler runs every 30 minutes on your connected inboxes.
          </p>
        </div>
        <Button variant="outline" className="rounded-none gap-2" onClick={runNow} data-testid="rules-run-now">
          <Play size={16} weight="duotone" /> Run rules now
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* --- Form: create or edit --- */}
        <form
          onSubmit={submit}
          className="border border-border bg-background p-6 space-y-4 lg:col-span-1"
          data-testid="rule-form"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Lightning size={20} weight="duotone" />
              <h2 className="font-display text-xl font-bold">
                {editingId ? "Edit rule" : "New rule"}
              </h2>
            </div>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                data-testid="rule-cancel-edit"
              >
                <X size={14} /> Cancel
              </button>
            )}
          </div>

          <div>
            <Label htmlFor="rule-name">Name</Label>
            <Input
              id="rule-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-none"
              placeholder="Delete promos after 30 days"
              data-testid="rule-name"
            />
          </div>

          <div>
            <Label>Sender contains</Label>
            <div className="space-y-2">
              <Select
                value={form.customSender ? "__custom__" : form.sender_contains}
                onValueChange={(v) => {
                  if (v === "__custom__") {
                    setForm({ ...form, sender_contains: "", customSender: true });
                  } else {
                    setForm({ ...form, sender_contains: v, customSender: false });
                  }
                }}
              >
                <SelectTrigger className="rounded-none" data-testid="rule-sender-select">
                  <SelectValue placeholder="Select a sender…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any sender</SelectItem>
                  {topSenders.map((s) => {
                    const key = s.sender_email || s.sender_name;
                    if (!key) return null;
                    return (
                      <SelectItem key={key} value={key}>
                        {s.sender_name || s.sender_email}{" "}
                        <span className="text-muted-foreground">· {s.count}</span>
                      </SelectItem>
                    );
                  })}
                  <SelectItem value="__custom__">Custom / Write in</SelectItem>
                </SelectContent>
              </Select>
              {form.customSender && (
                <Input
                  placeholder="Enter sender email or domain"
                  value={form.sender_contains}
                  onChange={(e) => setForm({ ...form, sender_contains: e.target.value })}
                  className="rounded-none"
                  data-testid="rule-custom-sender"
                  autoFocus
                />
              )}
            </div>
            <p className="text-[10px] font-mono text-muted-foreground mt-1 uppercase tracking-widest">
              populated from your top senders analytics
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rule-days">Older than (days)</Label>
              <Input
                id="rule-days"
                type="number"
                min={1}
                max={3650}
                value={form.older_than_days}
                onChange={(e) =>
                  setForm({ ...form, older_than_days: parseInt(e.target.value, 10) || "" })
                }
                className="rounded-none"
                data-testid="rule-days"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm({ ...form, category: v })}
              >
                <SelectTrigger className="rounded-none" data-testid="rule-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full rounded-none btn-brutalist gap-2"
            data-testid="rule-submit-btn"
          >
            {editingId ? (
              <>
                <PencilSimple size={18} /> Save changes
              </>
            ) : (
              <>
                <Plus size={18} /> Create rule
              </>
            )}
          </Button>
        </form>

        {/* --- Rules list --- */}
        <div className="lg:col-span-2 space-y-3">
          {rules.map((r) => (
            <div
              key={r.rule_id}
              className="border border-border bg-background p-5 flex items-start gap-4"
              data-testid={`rule-${r.rule_id}`}
            >
              <Lightning size={22} weight="duotone" className="text-primary mt-1" />
              <div className="flex-1">
                <div className="font-display text-lg font-bold">{r.name}</div>
                <div className="text-xs font-mono text-muted-foreground uppercase tracking-widest mt-1">
                  category={categoryDisplay(r)} · older_than={r.older_than_days}d ·{" "}
                  {senderDisplay(r)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => dryRun(r)}
                  title="Preview matches"
                  data-testid={`rule-preview-${r.rule_id}`}
                >
                  <Eye size={18} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => beginEdit(r)}
                  title="Edit rule"
                  data-testid={`rule-edit-${r.rule_id}`}
                >
                  <PencilSimple size={18} />
                </Button>
                <Switch
                  checked={r.active}
                  onCheckedChange={() => toggle(r.rule_id)}
                  data-testid={`rule-toggle-${r.rule_id}`}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => remove(r.rule_id)}
                  data-testid={`rule-delete-${r.rule_id}`}
                >
                  <Trash size={18} />
                </Button>
              </div>
            </div>
          ))}
          {rules.length === 0 && (
            <div className="border border-border p-12 text-center text-muted-foreground bg-background">
              No rules yet. Create one →
            </div>
          )}
        </div>
      </div>

      {/* --- Dry-run preview dialog --- */}
      <Dialog open={!!preview || previewLoading} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-2xl rounded-none" data-testid="rule-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Preview: {preview?.rule_name || "…"}
            </DialogTitle>
          </DialogHeader>
          {previewLoading && (
            <div className="py-12 text-center text-muted-foreground">Calculating matches…</div>
          )}
          {preview && (
            <>
              <div className="text-sm space-y-2">
                <div className="font-mono uppercase tracking-widest text-xs text-muted-foreground">
                  filters
                </div>
                <div className="border border-border p-3 bg-muted/30 font-mono text-xs space-y-1">
                  <div>
                    older_than_days:{" "}
                    <span className="font-bold">
                      {preview.filters_applied?.older_than_days}
                    </span>
                  </div>
                  <div>
                    category:{" "}
                    <span className="font-bold">
                      {preview.filters_applied?.category || "any"}
                    </span>
                  </div>
                  <div>
                    sender_contains:{" "}
                    <span className="font-bold">
                      {preview.filters_applied?.sender_contains || "any"}
                    </span>
                  </div>
                </div>
                <div className="text-3xl font-display font-bold mt-4" data-testid="rule-preview-count">
                  {preview.match_count} email{preview.match_count === 1 ? "" : "s"} would be deleted
                </div>
                {preview.samples?.length > 0 && (
                  <>
                    <div className="font-mono uppercase tracking-widest text-xs text-muted-foreground mt-4">
                      sample (first {preview.samples.length})
                    </div>
                    <div className="border border-border divide-y divide-border max-h-64 overflow-y-auto">
                      {preview.samples.map((s) => (
                        <div key={s.email_id} className="p-2 text-xs" data-testid={`preview-sample-${s.email_id}`}>
                          <div className="font-bold truncate">{s.subject}</div>
                          <div className="text-muted-foreground truncate">
                            {s.sender_name} &lt;{s.sender_email}&gt; · {s.category} ·{" "}
                            {new Date(s.received_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-none" onClick={() => setPreview(null)} data-testid="rule-preview-close">
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
